import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'sonner';
import { Bot } from 'lucide-react';
import SettingsCard from '../../Settings/SettingsCard';
import { useFeatures } from '../../../../context/FeatureContext';
import { getAutomationPolicy, putAutomationPolicy } from '../../../../services/ReconciliationActionService';

/**
 * Inställningar → Automatisering: what Mats may carry out on a bank line
 * without the user approving it first. A card of its own, because it governs
 * the agent only; rules and the classes of "Automatisk bokföring" govern the
 * matching engine. The two share one brake, the Nödstopp in that card.
 *
 * One switch per action. The backend also keeps a list of accepted kinds of
 * proof (`evidence_profiles`) that must contain the action's proof before it
 * runs unattended; the card derives that list from the switches, so a switch
 * that is on is on. A row therefore shows as on only when both are stored.
 *
 * Every save re-reads the policy first and changes one thing in it, so a
 * Nödstopp or class change made in the card above is never written back stale.
 * For the same reason the card does not show the Nödstopp's state itself.
 *
 * Purchases and bookings from a proposal are always approved by the user
 * (backend rule), so they have no row; their stored values are sent back as is.
 */
const ACTIONS = [
    { type: 'register_customer_payment', proof: 'invoice_reference', label: 'Registrera kundbetalning',
        when: 'När bankhändelsens referens pekar ut exakt en kundfaktura.' },
    { type: 'register_supplier_payment', proof: 'invoice_reference', label: 'Registrera leverantörsbetalning',
        when: 'När referensen pekar ut exakt en leverantörsfaktura som är bokförd som leverantörsskuld.' },
    { type: 'link_existing_payment', proof: 'recorded_payment_reference', label: 'Koppla redan registrerad betalning',
        when: 'När referensen pekar ut fakturan och exakt en registrerad betalning har samma belopp.' },
    { type: 'link_existing_verification', proof: 'existing_record_relationship', label: 'Koppla befintligt verifikat',
        when: 'När referensen pekar ut exakt ett verifikat.' },
    // Shown only with the payment_files entitlement; its stored value is sent back as is either way.
    { type: 'settle_payment_rows', proof: 'existing_record_relationship', label: 'Stäm av betalfil', feature: 'payment_files',
        when: 'När bankens återrapport (camt.054) pekar ut raderna i en betalfil som debiteringen täcker.' },
    { type: 'attach_evidence', proof: 'existing_record_relationship', label: 'Bifoga underlag', noCap: true,
        when: 'När underlaget redan hör till samma post.' },
];
const MANAGED_PROOFS = [...new Set(ACTIONS.map((a) => a.proof))];

const isOn = (policy, action) => !!policy.actions[action.type]?.enabled && policy.evidence_profiles.includes(action.proof);
const capText = (policy, action) => {
    const cap = policy.actions[action.type]?.cap_ore;
    return cap == null ? '' : String(cap / 100);
};
const toEdits = (policy) => Object.fromEntries(ACTIONS.map((a) => [a.type, { enabled: isOn(policy, a), cap: capText(policy, a) }]));

// The document to store: what the card shows, with `change` applied on top.
function toRequest(policy, change = {}) {
    const rows = { ...toEdits(policy), ...change.rows };
    const actions = Object.fromEntries(Object.entries(policy.actions).map(([type, a]) => [type, { enabled: a.enabled, cap_ore: a.cap_ore }]));
    for (const a of ACTIONS) {
        if (!actions[a.type]) continue;
        const cap = rows[a.type].cap === '' ? null : Math.round(Number(rows[a.type].cap) * 100);
        actions[a.type] = { enabled: rows[a.type].enabled, cap_ore: a.noCap ? null : cap };
    }
    const proofs = ACTIONS.filter((a) => rows[a.type].enabled).map((a) => a.proof);
    return {
        revision: policy.revision,
        enabled: change.enabled ?? policy.enabled,
        kill_switch: policy.kill_switch,
        allow_partial_payments: change.allow_partial_payments ?? policy.allow_partial_payments,
        evidence_profiles: [...new Set([...policy.evidence_profiles.filter((p) => !MANAGED_PROOFS.includes(p)), ...proofs])],
        actions,
    };
}

const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' };
const titleStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' };
const helpStyle = { fontSize: '0.75rem', color: '#6b7280' };

export default function AutomationPolicySettings({ companyId, canEdit = true }) {
    const { features } = useFeatures();
    const [policy, setPolicy] = useState(null);
    const [edits, setEdits] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null); // 'enabled' | 'partials' | action type

    const show = useCallback((next) => { setPolicy(next); setEdits(toEdits(next)); }, []);

    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        getAutomationPolicy(companyId)
            .then((p) => { if (active) show(p); })
            // 403/404: the feature is absent for this company — no card.
            .catch((e) => { if (active && ![403, 404].includes(e.response?.status)) setError('Kunde inte läsa inställningarna för Mats.'); });
        return () => { active = false; };
    }, [companyId, show]);

    const save = async (key, change, message) => {
        setSaving(key);
        try {
            const current = await getAutomationPolicy(companyId);
            const saved = await putAutomationPolicy(companyId, toRequest(current, change));
            // Rows with changes that are not saved yet keep them.
            const before = toEdits(policy);
            const unsaved = Object.fromEntries(Object.entries(edits).filter(([type, e]) => type !== key
                && (e.enabled !== before[type].enabled || e.cap !== before[type].cap)));
            setPolicy(saved);
            setEdits({ ...toEdits(saved), ...unsaved });
            toast.success(message);
        } catch (e) {
            toast.error(e.response?.status === 403
                ? 'Endast företagets ägare kan ändra vad Mats får göra själv.'
                : e.response?.data?.message || 'Inställningen kunde inte sparas.');
        } finally {
            setSaving(null);
        }
    };

    if (!policy) {
        return error ? <Alert variant="warning" className="mb-0 py-2" style={{ fontSize: '0.8125rem' }}>{error}</Alert> : null;
    }

    const baseline = toEdits(policy);
    const locked = !canEdit || saving !== null;
    const setRow = (type, patch) => setEdits((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));

    return (
        <SettingsCard
            icon={Bot}
            title="Mats: bankavstämning utan godkännande"
            subtitle="Mats föreslår alltid hur en bankhändelse ska hanteras. Här väljer du vad han får genomföra själv; allt annat väntar på ditt godkännande i Att göra. Från start får han inte göra något själv."
        >
            <div style={helpStyle}>
                Gäller bara Mats; reglerna och den automatiska bokföringen ovan är motorns. Nödstoppet ovan stoppar även Mats.
                {' '}Den som senast ändrade inställningarna här står som ansvarig för det Mats gör själv.
                {!canEdit && ' Endast företagets ägare kan ändra dem.'}
            </div>

            <div style={{ ...rowStyle, borderBottom: '1px solid #f3f4f6', marginTop: 4 }}>
                <div style={{ flex: 1 }}>
                    <div style={titleStyle}>Låt Mats genomföra åtgärder själv</div>
                    <div style={helpStyle}>
                        {policy.enabled
                            ? 'Mats genomför de åtgärder som är påslagna nedan, upp till beloppsgränsen.'
                            : 'Mats genomför ingenting utan ditt godkännande, oavsett valen nedan.'}
                    </div>
                </div>
                <Form.Check
                    type="switch"
                    id="mats-policy-enabled"
                    aria-label="Låt Mats genomföra åtgärder själv"
                    checked={policy.enabled}
                    disabled={locked}
                    onChange={(e) => save('enabled', { enabled: e.target.checked }, e.target.checked
                        ? 'Mats får nu genomföra påslagna åtgärder själv.'
                        : 'Mats väntar nu alltid på ditt godkännande.')}
                    label={saving === 'enabled' ? <Spinner animation="border" size="sm" /> : (policy.enabled ? 'På' : 'Av')}
                />
            </div>

            <div style={{ overflowX: 'auto' }}>
                <Table className="mb-0 align-middle" style={{ fontSize: '0.8125rem' }}>
                    <thead>
                        <tr style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
                            <th style={{ paddingLeft: 0 }}>Åtgärd</th>
                            <th>Högst (kr)</th>
                            <th className="text-center">Själv</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {ACTIONS.filter((a) => policy.actions[a.type] && (!a.feature || features?.[a.feature])).map((a) => {
                            const edit = edits[a.type] || baseline[a.type];
                            const stored = policy.actions[a.type];
                            const capNumber = Number(edit.cap);
                            const badCap = !a.noCap && edit.enabled && (edit.cap === '' || !Number.isFinite(capNumber) || capNumber <= 0);
                            const dirty = edit.enabled !== baseline[a.type].enabled || (!a.noCap && edit.cap !== baseline[a.type].cap);
                            return (
                                <tr key={a.type}>
                                    <td style={{ paddingLeft: 0 }}>
                                        <div style={{ fontWeight: 500 }}>{a.label}</div>
                                        <div style={helpStyle}>{a.when}</div>
                                    </td>
                                    <td style={{ minWidth: 130 }}>
                                        {a.noCap ? <span style={helpStyle}>Inget belopp</span> : (
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                min="0"
                                                step="100"
                                                aria-label={`Beloppsgräns ${a.label}`}
                                                value={edit.cap}
                                                // The proposed limit is visible before the switch is on.
                                                placeholder={stored.default_cap_ore ? String(stored.default_cap_ore / 100) : ''}
                                                isInvalid={badCap}
                                                disabled={locked}
                                                onChange={(e) => setRow(a.type, { cap: e.target.value })}
                                                style={{ maxWidth: 130 }}
                                            />
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <Form.Check
                                            type="switch"
                                            id={`mats-policy-${a.type}`}
                                            aria-label={`${a.label} utan godkännande`}
                                            checked={edit.enabled}
                                            disabled={locked}
                                            onChange={(e) => setRow(a.type, {
                                                enabled: e.target.checked,
                                                // Switching on proposes the default limit; the user can change it before saving.
                                                ...(e.target.checked && !a.noCap && edit.cap === '' && stored.default_cap_ore
                                                    ? { cap: String(stored.default_cap_ore / 100) } : {}),
                                            })}
                                        />
                                    </td>
                                    <td className="text-end" style={{ paddingRight: 0 }}>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            disabled={!dirty || badCap || locked}
                                            onClick={() => save(a.type, { rows: { [a.type]: edit } }, edit.enabled
                                                ? `${a.label}: Mats får göra det själv.`
                                                : `${a.label}: kräver ditt godkännande.`)}
                                        >
                                            {saving === a.type ? <Spinner animation="border" size="sm" /> : 'Spara'}
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <div style={{ ...rowStyle, paddingBottom: 0 }}>
                <div style={{ flex: 1 }}>
                    <div style={titleStyle}>Delbetalningar</div>
                    <div style={helpStyle}>Låt Mats registrera en betalning som är mindre än fakturans belopp. Annars godkänner du dem.</div>
                </div>
                <Form.Check
                    type="switch"
                    id="mats-policy-partials"
                    aria-label="Delbetalningar utan godkännande"
                    checked={policy.allow_partial_payments}
                    disabled={locked}
                    onChange={(e) => save('partials', { allow_partial_payments: e.target.checked }, e.target.checked
                        ? 'Mats får registrera delbetalningar själv.'
                        : 'Delbetalningar kräver ditt godkännande.')}
                    label={saving === 'partials' ? <Spinner animation="border" size="sm" /> : (policy.allow_partial_payments ? 'På' : 'Av')}
                />
            </div>

            <div style={{ ...helpStyle, marginTop: 12 }}>
                Köp och bokföring från förslag godkänns alltid av dig.
            </div>
        </SettingsCard>
    );
}
